import React, { useEffect, useState } from "react";
import { db } from "../firebase";
import { collection, onSnapshot, doc, deleteDoc, updateDoc } from "firebase/firestore";

const AdminProductList = ({ onEdit }) => {
  const [products, setProducts] = useState([]);
  const [allCategories, setAllCategories] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [viewMode, setViewMode] = useState("cards"); // "cards" o "compacta"

  useEffect(() => {
    const unsubscribe = onSnapshot(collection(db, "productos"), (snapshot) => {
      setProducts(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const unsub = onSnapshot(collection(db, "categorias"), (snapshot) => {
      setAllCategories(snapshot.docs.map(doc => doc.data().name));
    });
    return () => unsub();
  }, []);

  const handleDelete = async (id) => {
    if (window.confirm("¿Seguro de eliminar este producto?")) {
      await deleteDoc(doc(db, "productos", id));
    }
  };

  const handleToggle = async (id, field, value) => {
    await updateDoc(doc(db, "productos", id), { [field]: value });
  };

  // Filtrar por nombre y categoría
  const filtered = products.filter((p) => {
    const matchesName = p.name?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = !categoryFilter || p.category === categoryFilter;
    return matchesName && matchesCategory;
  });

  return (
    <div className="w-full">
      {/* Filtros */}
      <div className="flex flex-col md:flex-row gap-4 items-center mb-6">
        <input
          type="text"
          placeholder="Buscar producto por nombre..."
          className="w-full md:w-72 p-2 border rounded"
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
        />
        <select
          className="w-full md:w-56 p-2 border rounded"
          value={categoryFilter}
          onChange={e => setCategoryFilter(e.target.value)}
        >
          <option value="">Todas las categorías</option>
          {allCategories.map(cat => (
            <option key={cat} value={cat}>{cat}</option>
          ))}
        </select>
        <button
          onClick={() => setViewMode(viewMode === "cards" ? "compacta" : "cards")}
          className="p-2 px-5 border rounded bg-gray-100 hover:bg-gray-200"
        >
          Vista {viewMode === "cards" ? "Compacta" : "Tarjetas"}
        </button>
      </div>
      {/* Lista */}
      {viewMode === "compacta" ? (
        // Vista compacta tipo tabla
        <div className="overflow-x-auto">
          <table className="min-w-full bg-white rounded-xl shadow border">
            <thead>
              <tr>
                <th className="py-2 px-2">Imagen</th>
                <th className="py-2 px-2">Nombre</th>
                <th className="py-2 px-2">Precio</th>
                <th className="py-2 px-2">Categoría</th>
                <th className="py-2 px-2">Nuevo</th>
                <th className="py-2 px-2">Destacado</th>
                <th className="py-2 px-2">Editar</th>
                <th className="py-2 px-2">Eliminar</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((product) => (
                <tr key={product.id} className="border-t hover:bg-blue-50">
                  <td className="py-2 px-2 text-center">
                    <img src={product.image} alt={product.name} className="w-14 h-14 object-cover rounded" />
                  </td>
                  <td className="py-2 px-2">{product.name}</td>
<td className="py-2 px-2">
  {Number(product.price).toLocaleString("es-CO", { style: "currency", currency: "COP", minimumFractionDigits: 0 })}
</td>
                  <td className="py-2 px-2">{product.category || <span className="text-gray-400">Sin categoría</span>}</td>
                  <td className="py-2 px-2 text-center">
                    <input
                      type="checkbox"
                      checked={!!product.isNew}
                      onChange={e => handleToggle(product.id, "isNew", e.target.checked)}
                    />
                  </td>
                  <td className="py-2 px-2 text-center">
                    <input
                      type="checkbox"
                      checked={!!product.isFeatured}
                      onChange={e => handleToggle(product.id, "isFeatured", e.target.checked)}
                    />
                  </td>
                  <td className="py-2 px-2">
                    <button
                      className="bg-yellow-400 text-white px-3 py-1 rounded hover:bg-yellow-500"
                      onClick={() => onEdit(product)}
                    >
                      Editar
                    </button>
                  </td>
                  <td className="py-2 px-2">
                    <button
                      className="bg-red-500 text-white px-3 py-1 rounded hover:bg-red-700"
                      onClick={() => handleDelete(product.id)}
                    >
                      Eliminar
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        // Vista tarjetas moderna
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filtered.map((product) => (
            <div key={product.id} className="bg-white rounded-2xl shadow-lg p-6 flex flex-col items-center border hover:shadow-2xl transition relative">
              {/* BADGES */}
              <div className="absolute top-4 left-4 flex flex-col gap-2">
                {product.isNew && (
                  <span className="bg-green-500 text-white text-xs px-3 py-1 rounded-full font-semibold shadow">
                    Nuevo
                  </span>
                )}
                {product.isFeatured && (
                  <span className="bg-yellow-500 text-white text-xs px-3 py-1 rounded-full font-semibold shadow">
                    Destacado
                  </span>
                )}
              </div>
              <img
                src={product.image}
                alt={product.name}
                className="w-28 h-28 object-cover rounded-lg border mb-3"
              />
              <h3 className="text-xl font-bold text-primary mb-1 text-center">{product.name}</h3>
<td className="py-2 px-2">
  {Number(product.price).toLocaleString("es-CO", { style: "currency", currency: "COP", minimumFractionDigits: 0 })}
</td>
              <div className="mb-3">
                {product.category ? (
                  <span className="inline-block bg-blue-100 text-blue-700 text-xs font-semibold px-3 py-1 rounded-full">
                    {product.category}
                  </span>
                ) : (
                  <span className="inline-block bg-gray-200 text-gray-500 text-xs font-semibold px-3 py-1 rounded-full">
                    Sin categoría
                  </span>
                )}
              </div>
              {/* Switches */}
              <div className="flex gap-6 mb-2">
                <label className="flex items-center gap-1 text-xs">
                  <input
                    type="checkbox"
                    checked={!!product.isNew}
                    onChange={e => handleToggle(product.id, "isNew", e.target.checked)}
                  />
                  Nuevo
                </label>
                <label className="flex items-center gap-1 text-xs">
                  <input
                    type="checkbox"
                    checked={!!product.isFeatured}
                    onChange={e => handleToggle(product.id, "isFeatured", e.target.checked)}
                  />
                  Destacado
                </label>
              </div>
              <div className="text-xs text-gray-400 mb-3 text-center line-clamp-2">{product.description}</div>
              <div className="flex gap-3 mt-auto">
                <button
                  className="bg-yellow-400 text-white font-bold px-4 py-2 rounded-lg hover:bg-yellow-500"
                  onClick={() => onEdit(product)}
                >
                  Editar
                </button>
                <button
                  className="bg-red-500 text-white font-bold px-4 py-2 rounded-lg hover:bg-red-700"
                  onClick={() => handleDelete(product.id)}
                >
                  Eliminar
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminProductList;
